import { Stack, Typography, Button, Skeleton } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigationStore } from '../../../../../store';
import {
  DestinationIcon,
  RouteStartIcon,
  TurnLeftIcon,
  TurnRightIcon,
  ElevatorIcon,
  StairsIcon,
  StraightArrowIcon,
} from '@/components/navigation/icons/Icons';
import { navigationTranslationState } from '../../../../../store';
import { trimDescription } from '@/components/navigation/search-bar/util';

type DetailedRoutesProps = {
  setNavigationDestination: (item: any, type: string, isPreload: boolean) => void;
  item: any;
  isPreload: boolean;
  detailedRoutes?: any[];
  startingPoint?: any;
  showDetailedRoutesOnly?: boolean;
  rtl?: boolean;
  navigationType: string;
  showCaseTemp?: any;
};

const directionIconsMap = {
  left: <TurnLeftIcon />,
  right: <TurnRightIcon />,
};

const teleportIconsMap = {
  elevator: <ElevatorIcon />,
  stairway: <StairsIcon />,
};

export const DetailedRoutes = ({
  setNavigationDestination,
  item,
  isPreload,
  detailedRoutes,
  startingPoint,
  showDetailedRoutesOnly,
  rtl,
  navigationType,
  showCaseTemp,
}: DetailedRoutesProps) => {
  const stackRef = useRef(null);
  const routeRefs = useRef({});
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const [highlightedItem, setHighlightedItem] = useState(null);
  const [waitingItem, setWaitingItem] = useState(null);
  const translation = useNavigationStore((state) => state.navigationTranslation);

  useEffect(() => {
    if (isPreload) {
      const navType = navigationType || 'shortest';
      setNavigationDestination(item, navType, isPreload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, isPreload, startingPoint, navigationType]);

  useEffect(() => {
    setHighlightedItem(null);
    setWaitingItem(null);
    routeRefs.current = {};
  }, [detailedRoutes]);

  const renderRouteDetails = useCallback((route, index) => {
    if (index === 0) {
      return (
        <>
          <Stack width="24px" height="24px">
            <RouteStartIcon />
          </Stack>
          <Typography
            fontSize="16px"
            fontWeight="400"
            textAlign="start"
            fontFamily="Inter"
            whiteSpace="break-spaces"
            textOverflow="ellipsis"
            color="#000000"
            sx={{ wordBreak: 'break-word', direction: rtl ? 'rtl' : '' }}
          >
            <>
              {translation?.navStartAt || 'Start at'}{' '}
              <strong>
                {trimDescription(
                  route?.tag?.title || route?.station?.title || route?.title || route?.name,
                  50,
                )}
              </strong>
            </>
          </Typography>
        </>
      );
    }
    if (route.turn) {
      return (
        <>
          <Stack width="24px" height="24px">
            {directionIconsMap[route.turn]}
          </Stack>
          <Typography
            fontSize="16px"
            fontWeight="400"
            textAlign="start"
            color="#000000"
            sx={{ direction: rtl ? 'rtl' : '' }}
          >
            <>
              {translation?.navWalk || 'Walk'} {route.distance}m <br />
              {translation?.navThen || 'Then'} {translation?.navTurn || 'turn'}{' '}
              <strong>
                {route.turn === 'left'
                  ? `${translation?.navLeft || route.turn}`
                  : `${translation?.navRight || route.turn}`}
              </strong>
            </>
          </Typography>
        </>
      );
    }

    if (!!route?.tpGroupStart && !!route?.tpGroupEnd) {
      return (
        <>
          <Stack width="24px" height="24px">
            {teleportIconsMap[route.tpGroupStart.type]}
          </Stack>
          <Typography
            fontSize="16px"
            fontWeight="400"
            textAlign="start"
            color="#000000"
            sx={{ direction: rtl ? 'rtl' : '' }}
          >
            <>
              {translation?.navWalk || 'Walk'} {route.distance}m <br />
              {translation?.navThen || 'Then'}{' '}
              <strong>
                {route.tpGroupStart.type === 'elevator'
                  ? translation?.navEnterToTheElevator || 'enter elevator'
                  : translation?.navTakeTheStairs || 'take stairs'}
                ,
              </strong>
            </>
            <br />
            <>
              {route.tpGroupStart.type === 'elevator'
                ? translation?.navSelect || 'Select'
                : 'Go to'}{' '}
              {route.tpGroupEnd.name}
              <strong>
                {route.tpGroupEnd.turn &&
                  `, ${translation?.navTurn || 'exit'} ${
                    route.tpGroupEnd.turn === 'left'
                      ? translation?.navLeft || route.tpGroupEnd.turn
                      : translation?.navRight || route.tpGroupEnd.turn
                  }`}
              </strong>
            </>
          </Typography>
        </>
      );
    }

    if (route.isMandatory) {
      return (
        <>
          <Stack width="24px" height="24px">
            <StraightArrowIcon />
          </Stack>
          <Typography
            fontSize="16px"
            fontWeight="400"
            textAlign="start"
            fontFamily="Inter"
            whiteSpace="break-spaces"
            textOverflow="ellipsis"
            color="#000000"
            sx={{ wordBreak: 'break-word', direction: rtl ? 'rtl' : '' }}
          >
            <>
              {translation?.navWalk || 'Walk'} {route.distance}m <br />
              {translation?.navThen || 'Then'}{' '}
              <strong>
                {translation?.navProceedStraightAt || 'proceed straight at'}{' '}
                {trimDescription(route?.title || route?.name, 50)}
              </strong>
            </>
          </Typography>
        </>
      );
    }

    if (route.isFinish) {
      return (
        <>
          <Stack width="24px" height="24px">
            <DestinationIcon />
          </Stack>
          <Typography
            fontSize="16px"
            fontWeight="400"
            textAlign="start"
            fontFamily="Inter"
            whiteSpace="break-spaces"
            textOverflow="ellipsis"
            color="#000000"
            sx={{ wordBreak: 'break-word', direction: rtl ? 'rtl' : '' }}
          >
            <>
              {translation?.navWalk || 'Walk'} {route.distance}m <br />
              <strong>
                {translation?.navYouHaveReached || 'You have reached'}{' '}
                {trimDescription(route?.station?.title || route?.title, 50)}
              </strong>
            </>
          </Typography>
        </>
      );
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkOverflow = () => {
      if (stackRef.current) {
        const { scrollHeight, clientHeight } = stackRef.current;
        setIsOverflowing(scrollHeight > clientHeight);
      }
    };
    if (detailedRoutes?.length > 0 || showDetailedRoutesOnly) {
      setTimeout(() => {
        checkOverflow();
      }, 1000);
    }

    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [detailedRoutes?.length, showDetailedRoutesOnly]);

  const scrollToBottom = () => {
    if (stackRef.current) {
      stackRef.current.scrollTo({
        top: stackRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const handleScroll = () => {
    if (stackRef.current) {
      const { scrollTop } = stackRef.current;
      setIsAtTop(scrollTop === 0);
    }
    if (stackRef.current) {
      clearTimeout(stackRef.current.timeout);
    }
  };

  useEffect(() => {
    if (!showCaseTemp?.showcase?.Sweep?.current) return;

    const handleSweepChange = (currentSweep) => {
      if (!currentSweep?.id || !detailedRoutes?.length) return;
      const foundItem = detailedRoutes.find(
        (r) =>
          r.id === currentSweep?.id ||
          r?.tpGroupEnd?.sweep === currentSweep?.id ||
          r?.tpGroupStart?.sweep === currentSweep?.id,
      );
      if (foundItem) {
        const foundItemIndex = detailedRoutes.findIndex((r) => r.id === foundItem.id);
        const nextWaitingItem = detailedRoutes[foundItemIndex + 1];

        setHighlightedItem(foundItem);
        setWaitingItem(nextWaitingItem);
      } else {
        const highlightedItemIndex = detailedRoutes.findIndex((r) => r.id === highlightedItem?.id);
        const nextItem = detailedRoutes[highlightedItemIndex + 1];
        // Only update if the next item is not already highlighted
        if (nextItem && nextItem?.id === waitingItem?.id) {
          setHighlightedItem(nextItem);
        }
      }
    };

    const subscription = showCaseTemp.showcase.Sweep.current.subscribe(handleSweepChange);

    // Cleanup function to unsubscribe
    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, [showCaseTemp, detailedRoutes, highlightedItem, waitingItem]);

  useEffect(() => {
    if (highlightedItem?.id && routeRefs.current[highlightedItem.id]) {
      routeRefs.current[highlightedItem.id].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [highlightedItem]);

  return (
    <Stack
      id="detailed-routes-wrapper"
      ref={stackRef}
      onScroll={handleScroll}
      sx={{
        borderTop: '1px solid #EAECF0',
        p: '14px',
        alignItems: 'center',
        maxHeight: showDetailedRoutesOnly ? '100%' : '',
        overflowY: 'auto',
        position: 'relative',
        '&::-webkit-scrollbar': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          width: '0px',
          backgroundColor: 'transparent',
          display: 'none',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        direction: rtl ? 'rtl' : 'ltr',
      }}
    >
      <Typography
        fontSize="16px"
        fontWeight="400"
        fontFamily="Inter"
        textAlign="start"
        width="100%"
        color="#000000"
        sx={{ mb: '11px', direction: rtl ? 'rtl' : '' }}
      >
        {translation.navDetailedRoute || 'Detailed route'}
      </Typography>
      {detailedRoutes?.length > 0
        ? detailedRoutes.map((route, i) => {
            const uniqueKey = route.id ? `${route.id}-${i}` : `route-${i}`;

            return (
              <Stack
                key={uniqueKey}
                ref={(el) => {
                  if (route.id) {
                    routeRefs.current[route.id] = el;
                  }
                }}
                direction="row"
                alignItems="flex-start"
                justifyContent="flex-start"
                sx={{
                  mb: '10px',
                  borderRadius: '8px',
                  border: '1px solid #EAECF0',
                  background:
                    (route?.id && highlightedItem?.id && route.id === highlightedItem.id) ||
                    (!highlightedItem && i === 0)
                      ? '#EAECF0'
                      : '#FFF',
                  p: '14px',
                  width: '100%',
                  gap: '10px',
                }}
              >
                {renderRouteDetails(route, i)}
              </Stack>
            );
          })
        : Array.from({ length: 4 }).map((_, i) => (
            <Stack
              key={i}
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              sx={{
                mb: '10px',
                borderRadius: '8px',
                border: '1px solid #EAECF0',
                background: '#FFF',
                p: '14px',
                width: '100%',
                gap: '10px',
              }}
            >
              <Skeleton variant="rectangular" height={24} width={311} />
            </Stack>
          ))}
      {isOverflowing && isAtTop && (
        <Stack
          sx={{
            background: ' linear-gradient(180deg, rgba(237, 237, 237, 0.00) 33.33%, #EDEDED 100%)',
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: { xs: '100%', sm: '340px' },
            height: '88px',
            alignItems: 'center',
            justifyContent: 'flex-end',
            pb: '10px',
            borderRadius: '0px 0px 8px 14px',
          }}
        >
          <Button
            onClick={scrollToBottom}
            startIcon={<i className="fas fa-arrow-down" style={{ fontSize: '12px', margin: 0 }} />}
            sx={{
              color: '#344054',
              p: '4px 10px 4px 12px',
              borderRadius: '16px',
              border: '1px solid #EAECF0',
              background: '#F9FAFB',
              '&:hover': {
                background: '#F9FAFB',
                color: '#344054',
              },
            }}
          >
            {translation?.navViewMore || 'View more'}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
